import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { PATH_SIGNUP } from '@farmersdog/constants/paths';
import {
  GridContainer,
  Link,
  Text,
  useViewport,
  type TextVariant,
} from '@farmersdog/corgi-x';

import { TextTypeRenderer } from '../../components/TextTypeRenderer';
import config from '../../config';
import {
  SliceType,
  EmailFormSlice,
  MetadataSlice,
  isSliceOfType,
} from '../../slices';
import { useRoutes } from '../../static-context';

import styles from './BreedsContentType.css';
import { BreedsLinks } from './BreedsLinks';
import { createBreedsSchema } from './createBreedsSchema';
import { getBreedRoutesFromRoutes } from './getBreedRoutesFromRoutes';

import type { BreedsDocument } from './types';

export interface BreedsContentTypeProps {
  document: BreedsDocument;
}

export function BreedsContentType({ document }: BreedsContentTypeProps) {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const emailFormSlice = document.data.body.find(
    isSliceOfType(SliceType.EmailFormSlice)
  );

  const metadataSlice = document.data.body.find(
    isSliceOfType(SliceType.MetadataSlice)
  );

  const viewport = useViewport();
  const isLargeViewport = hasMounted && viewport.lg;

  const titleVariant: TextVariant = isLargeViewport
    ? 'heading-52'
    : 'heading-40';

  const allRoutes = useRoutes();
  const baseUrl = config.get('app.websiteUrl');

  const breeds = getBreedRoutesFromRoutes(allRoutes).sort((a, b) =>
    a.url < b.url ? -1 : 1
  );

  const schema = createBreedsSchema({ breeds, baseUrl });

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      {metadataSlice && <MetadataSlice slice={metadataSlice} />}
      <GridContainer className={styles.grid}>
        <div className={styles.top}>
          <Text
            className={styles.heading}
            as="h1"
            variant={titleVariant}
            color="kale-3"
            weight="bold"
          >
            {document.data.h1}
          </Text>
        </div>
        <div className={styles.left}>
          <Text as="p" variant="article-20" color="charcoal-3">
            Our fresh recipes are good for dogs of all shapes, sizes, and
            breeds—from puppies to seniors. Below, you&apos;ll find helpful info
            and care tips for a long roster of breeds. Even if you don&apos;t
            see your dog&apos;s breed—or breed mix—on the list, we make complete
            and balanced food for them.{' '}
            <Link href={PATH_SIGNUP}>Sign up here</Link> to start feeding fresh.
          </Text>
          <TextTypeRenderer nodes={document.data.description} />
          <BreedsLinks routes={breeds} />
        </div>
        <div className={styles.right}>
          {emailFormSlice && <EmailFormSlice slice={emailFormSlice} />}
        </div>
      </GridContainer>
    </>
  );
}
